/**
 * APBreadcrumbs Component
 *
 * A breadcrumb navigation component following Figma design system specifications.
 * Supports clickable items, current item highlighting, and custom separators.
 *
 * @example
 * ```tsx
 * <APBreadcrumbs
 *   items={[
 *     { text: 'Home', href: '/', isClickable: true },
 *     { text: 'Settings', href: '/settings', isClickable: true },
 *     { text: 'Profile', isCurrent: true }
 *   ]}
 *   onItemClick={(item, index) => navigate(item.href)}
 * />
 * ```
 */

import { Fragment } from 'react';

import { useTranslate } from '@shared/hooks/use-translate';

import type { IAPBreadcrumbsProps, IBreadcrumbItem } from './types';

import APIconAstropay from '../../atoms/media/icon-ap';

import './styles.scss';

/**
 * Arrow separator component using APIconAstropay
 */
const ArrowRightSeparator = ({ className }: { className?: string }) => (
  <div className={`ap-breadcrumbs__separator ${className || ''}`} data-name="arrow/right_line">
    <APIconAstropay icon="arrow_right_fill" size="small" color="var(--ap-text-muted)" />
  </div>
);

/**
 * Default separator using the Figma arrow design
 */
const DefaultSeparator = () => <ArrowRightSeparator />;

/**
 * APBreadcrumbs - Navigation breadcrumb component
 *
 * Follows AstroPay design system and accessibility guidelines.
 */
export const APBreadcrumbs: React.FC<IAPBreadcrumbsProps> = ({
  items,
  onItemClick,
  separator = <DefaultSeparator />,
  className = '',
  'data-testid': testId,
}) => {
  const translate = useTranslate();

  const handleItemClick = (item: IBreadcrumbItem, index: number) => {
    if (item.isClickable && onItemClick) {
      onItemClick(item, index);
    }
  };

  const breadcrumbClasses = ['ap-breadcrumbs', className].filter(Boolean).join(' ');

  return (
    <nav
      className={breadcrumbClasses}
      aria-label={translate('aria.breadcrumb-navigation')}
      data-testid={testId}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const itemClasses = [
          'ap-breadcrumbs__item',
          item.isClickable && 'ap-breadcrumbs__item--clickable',
        ]
          .filter(Boolean)
          .join(' ');

        const textClasses = [
          'ap-breadcrumbs__text',
          item.isCurrent && 'ap-breadcrumbs__text--current',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <Fragment key={`breadcrumb-${index}`}>
            <div className={itemClasses}>
              {item.isClickable ? (
                <button
                  type="button"
                  className={textClasses}
                  onClick={() => handleItemClick(item, index)}
                  aria-current={item.isCurrent ? 'page' : undefined}
                >
                  {item.text}
                </button>
              ) : (
                <p className={textClasses} aria-current={item.isCurrent ? 'page' : undefined}>
                  {item.text}
                </p>
              )}
            </div>
            {!isLast && separator}
          </Fragment>
        );
      })}
    </nav>
  );
};

APBreadcrumbs.displayName = 'APBreadcrumbs';

export default APBreadcrumbs;
